import React from 'react';
import { Box, CardMedia, Stack, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import { Image } from '@components/Image';
import { ROUTES } from '@routes/routes';
import { useTranslation, Trans } from 'react-i18next';
import { formatLocaleDateShort } from '@helpers/dateHelpers';
import { ActivityOverlayChip } from '../../../components/ActivityOverlayChip';
import { InfoSection } from '../../../components/InfoSection';
import { RankingPodiumOverlay } from '@pages/Portal/Competitions/Results/Overview/components/RankingPodiumOverlay';
import {
  CompetitionFormat,
  PortalRankingEntry,
} from '@api/competitionsApi/interfaces';
import { CompetitionActivityVariant } from '../CompetitionActivityBody/interfaces';
import { WeaponGroup } from '@api/disciplinesApi/interfaces';
import { WeaponGroupChips } from '@components/competitions/WeaponGroupChips';
import VerifiedIcon from '@mui/icons-material/Verified';

type Props = {
  coverImageId: string;
  title: string;
  variant: CompetitionActivityVariant;
  startsOn?: string;
  endsOn?: string;
  disciplineName?: string;
  weaponGroups?: WeaponGroup[];
  format?: CompetitionFormat;
  podium?: PortalRankingEntry[];
  enrollmentCount?: number;
};

export const CompetitionActivityCoverImage: React.FC<Props> = ({
  coverImageId,
  title,
  variant,
  startsOn,
  endsOn,
  disciplineName,
  weaponGroups = [],
  format,
  podium,
  enrollmentCount = 0,
}): JSX.Element => {
  const { t } = useTranslation();

  const showPodium = variant === 'completed' && podium !== undefined;

  const formattedStartsOn = startsOn
    ? formatLocaleDateShort(startsOn)
    : null;
  const formattedEndsOn = endsOn ? formatLocaleDateShort(endsOn) : null;

  const formatLabel = format
    ? t(`competitions:manage.labels.format.${format}`)
    : null;

  const FormatIcon = format === 'session' ? GroupWorkIcon : PersonOutlineIcon;
  const bold = <strong />;

  const badgeKey = ((): string => {
    switch (variant) {
      case 'published':
        return 'activities:competition.badge.new';
      case 'completed':
        return 'activities:competition.badge.completed';
      case 'stopped':
        return 'activities:competition.badge.stopped';
      case 'resumed':
        return 'activities:competition.badge.resumed';
      default:
        return 'activities:competition.badge.started';
    }
  })();

  return (
    <Box sx={{ mb: 1 }}>
      <CardMedia
        component="div"
        sx={{
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Image
          imageSrc={ROUTES.images.image(coverImageId)}
          aspectRatio={700 / 300}
          sx={{
            mt: 0,
            border: 'none',
            borderRadius: 0,
          }}
        />

        {!showPodium && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.15) 45%, rgba(0, 0, 0, 0.35) 100%)',
              pointerEvents: 'none',
            }}
          />
        )}

        {showPodium && (
          <RankingPodiumOverlay
            podium={podium}
            enrollmentCount={enrollmentCount}
            size="default"
          />
        )}

        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            right: 12,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              px: 1,
              py: 0.25,
              mb: 0.75,
              borderRadius: '999px',
              backgroundColor: 'secondary.main',
              color: 'common.white',
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {t(badgeKey)}
          </Box>

          <Typography
            variant="h5"
            sx={{
              color: 'common.white',
              fontWeight: 700,
              fontSize: '1.35rem',
              lineHeight: 1.2,
              textShadow: '0 1px 4px rgba(0, 0, 0, 0.45)',
            }}
          >
            {title}
          </Typography>
        </Box>

        {!showPodium && (
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              position: 'absolute',
              left: 12,
              bottom: 12,
              flexWrap: 'wrap',
              gap: 0.75,
            }}
          >
            {disciplineName && (
              <ActivityOverlayChip
                icon={<GpsFixedIcon />}
                label={
                  <Trans
                    i18nKey="activities:competition.discipline"
                    values={{ name: disciplineName }}
                    components={{ bold }}
                  />
                }
              />
            )}
            {formatLabel && (
              <ActivityOverlayChip
                icon={<FormatIcon />}
                label={
                  <Trans
                    i18nKey="activities:competition.kind"
                    values={{ kind: formatLabel }}
                    components={{ bold }}
                  />
                }
              />
            )}
          </Stack>
        )}
      </CardMedia>

      {weaponGroups.length > 0 && (
        <InfoSection icon={<VerifiedIcon />}>
          <WeaponGroupChips weaponGroups={weaponGroups} />
        </InfoSection>
      )}

      {formattedStartsOn && formattedEndsOn && (
        <InfoSection icon={<CalendarMonthIcon />}>
          <Trans
            i18nKey="activities:competition.period"
            values={{
              startsOn: formattedStartsOn,
              endsOn: formattedEndsOn,
            }}
            components={{ bold }}
          />
        </InfoSection>
      )}
    </Box>
  );
};
